import {DoseCalcModeActive, VolumeMode, CatalogType} from '@cypro/engine/database/schema'
import {MedicineSchema, TherapySequenceItemSchema} from './schemas'
import {uuidv7} from 'uuidv7'
import {
  DrugCatalogItem,
  ActiveSubstanceDetail,
  SupportiveMedicationDetail,
  ProtocolTherapySequence,
} from '@cypro/engine/schema'

export function getNewFormTherapySequences(
  oldValue: TherapySequenceItemSchema[],
  newItem: TherapySequenceItemSchema,
  index: number
) {
  if (index < 0) {
    return [...oldValue, newItem]
  }

  return oldValue.map((item, i) => {
    if (i === index) return newItem
    return item
  })
}

// The entry switches live on the medicines: trade names are shown when one has a prescription trade name (CY-381)
export function getEntryTradeNameFlags(medicine: Pick<MedicineSchema, 'prescribedNameId' | 'autIdem'>[]) {
  const usePrescribedNames = medicine.some(m => m.prescribedNameId.length > 0)
  return {usePrescribedNames, autIdem: usePrescribedNames && medicine.some(m => Boolean(m.autIdem))}
}

// Copies the entry's No Aut Idem switch to every medicine, since only medicine[].autIdem is saved (CY-381)
export function withEntryAutIdem(entry: TherapySequenceItemSchema): TherapySequenceItemSchema {
  const autIdem = Boolean(entry.usePrescribedNames && entry.autIdem)
  const medicine = entry.medicine.map(
    (m): MedicineSchema => ({
      id: m.id,
      activeSubstanceId: m.activeSubstanceId,
      prescribedNameId: m.prescribedNameId,
      autIdem,
      calculationMethod: m.calculationMethod,
      notes: m.notes,
      regularDose: m.regularDose,
      precisionDecimals: m.precisionDecimals,
      doseProportion: m.doseProportion,
      ageRanges: m.ageRanges,
    })
  )
  return {...entry, medicine}
}

export const getTherapySequenceItemDefaultValues = (
  existing: TherapySequenceItemSchema | undefined,
  isNote: boolean = false,
  dayZeroProtocol?: boolean | null
): TherapySequenceItemSchema => {
  return {
    id: existing?.id ?? uuidv7(),
    day: existing?.day ? existing.day : dayZeroProtocol ? 0 : 1,
    time: existing?.time ?? '',
    isNote: isNote,
    note: existing?.note ?? '',
    carrierSolutionId: existing?.carrierSolutionId ?? ([] as string[]),
    applicationFormId: existing?.applicationFormId ?? ([] as string[]),
    duration: existing?.duration ?? 0,
    volume: existing?.volume ?? 0,
    dayZeroProtocol: dayZeroProtocol === undefined || dayZeroProtocol === null ? true : dayZeroProtocol,
    volumeMode: existing?.volumeMode ?? ([] as VolumeMode[]),
    ...getEntryTradeNameFlags(existing?.medicine ?? []),
    therapyNote: existing?.therapyNote ? existing.therapyNote : undefined,
    pharmacyNote: existing?.pharmacyNote ? existing.pharmacyNote : undefined,
    medicine: isNote
      ? []
      : (existing?.medicine ??
        ([
          {
            activeSubstanceId: [] as string[],
            prescribedNameId: [] as string[],
            calculationMethod: [] as DoseCalcModeActive[],
            regularDose: 0,
            precisionDecimals: 2,
            autIdem: false,
            notes: '',
            ageRanges: [],
          },
        ] as MedicineSchema[])),
  }
}

export function changeDoseUnitLabel(
  calculationMethod: DoseCalcModeActive,
  selectedActiveSubstance?: DrugCatalogItem,
  doseUnitDisplayName?: string
) {
  let label = doseUnitDisplayName || ''
  const details = selectedActiveSubstance ? getDrugDetails(selectedActiveSubstance) : undefined
  if (!doseUnitDisplayName && details) {
    label = details.doseUnit?.displayName || ''
  }

  switch (calculationMethod) {
    case DoseCalcModeActive.enum.BSA:
      return `${label}/m²`
    case DoseCalcModeActive.enum.WEIGHT:
      return `${label}/kg`
    case DoseCalcModeActive.enum.AUC:
      return `${label} * min/ml`
    case DoseCalcModeActive.enum.VOLUME:
      return `ml`
    case DoseCalcModeActive.enum.BSA30:
      return `${label}/m² (30 kg = 1 m²)`
    default:
      return label
  }
}

export const getDrugDetails = (
  drug: DrugCatalogItem | undefined
): ActiveSubstanceDetail | SupportiveMedicationDetail | undefined => {
  switch (drug?.catalogType) {
    case CatalogType.enum.ACTIVE_SUBSTANCE:
      return drug?.activeSubstanceDetails
    case CatalogType.enum.SUPPORTIVE_MEDICATION:
      return drug?.supportiveMedicationDetails
    default:
      return drug?.activeSubstanceDetails
  }
}

export const converTherapySchema = (ts: ProtocolTherapySequence[]): TherapySequenceItemSchema[] => {
  return ts.map(s => ({
    id: s.id,
    day: s.day,
    time: s.time,
    protocolId: s.protocolId,
    isNote: s.isNote || false,
    note: s.note || '',
    carrierSolutionId: s.carrierSolutionId ? [s.carrierSolutionId] : [],
    applicationFormId: s.applicationFormId ? [s.applicationFormId] : [],
    duration: s.duration ? s.duration : 0,
    volume: s.volume ? s.volume : 0,
    volumeMode: s.volumeMode ? [s.volumeMode] : [],
    medicine:
      s?.medicine?.map(m => ({
        id: m.id,
        ageRanges: [],
        autIdem: m.autIdem || false,
        notes: m.notes || '',
        regularDose: m.regularDose,
        precisionDecimals: m.precisionDecimals ?? 2,
        calculationMethod: m.calculationMethod ? [m.calculationMethod] : [],
        prescribedNameId: m.prescribedNameId ? [m.prescribedNameId] : [],
        activeSubstanceId: m.activeSubstanceId ? [m.activeSubstanceId] : [],
      })) || [],
  }))
}
